import {
  StrapiConditionDTO,
  ConditionDTO,
} from "@lib/strapi/dtos/content-types/condition.dto";
import { StrapiPages } from "@lib/strapi/pages";
import { StrapiResponse } from "@lib/strapi/dtos/core/response.dto";
import { StrapiRoutes } from "@lib/strapi/routes";
import { TreatmentService } from "@lib/strapi/services/content-types/treatment.service";
import { fetchAPI } from "@lib/strapi/fetch-api";
import { getImageProps, ImageProps } from "@lib/image";
import { parseStrapiContent } from "@lib/strapi/content";

export class ConditionService {
  static async fromStrapi(
    dto: ConditionDTO,
    locale: string,
    strapiData?: StrapiConditionDTO,
  ): Promise<ConditionDTO> {
    if (!strapiData) {
      return null;
    }
    if (dto == null) {
      dto = new ConditionDTO();
    }
    dto.title = strapiData.title;
    dto.slug = strapiData.slug;
    dto.teaser = strapiData.teaser;
    dto.description = await parseStrapiContent(strapiData.description);
    dto.symptoms = await parseStrapiContent(strapiData.symptoms);
    dto.sortIndex = strapiData.sortIndex;
    dto.coverImage = getImageProps(strapiData.coverImage);
    dto.treatments = await Promise.all(
      (strapiData.treatments ?? []).map(
        async (item) => await TreatmentService.fromStrapi(null, locale, item),
      ),
    );
    dto.seo = strapiData.seo;
    return dto;
  }

  static async fetchAll(
    authToken: string,
    locale: string,
  ): Promise<ConditionDTO[]> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = {
      locale,
      sort: ["sortIndex:asc"],
      pagination: { pageSize: 200 },
    };
    const response: StrapiResponse<StrapiConditionDTO[]> = await fetchAPI(
      StrapiRoutes.conditions(),
      urlParamsObject,
      token,
    );
    const dto = new ConditionDTO();
    return await Promise.all(
      (response.data ?? []).map(
        async (item) =>
          await ConditionService.fromStrapi(new ConditionDTO(), locale, item),
      ),
    );
  }

  static async fetchBySlug(
    authToken: string,
    locale: string,
    slug?: string,
  ): Promise<ConditionDTO> {
    const token = (process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN);
    const urlParamsObject = { locale, filters: { slug: slug } };
    const response: StrapiResponse<StrapiConditionDTO[]> = await fetchAPI(
      StrapiRoutes.conditions(),
      urlParamsObject,
      token,
    );
    const dto = new ConditionDTO();
    if ((response.data ?? []).length === 0) {
      return null;
    }
    return await ConditionService.fromStrapi(
      dto,
      locale,
      (response.data ?? [])[0],
    );
  }
}
